import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { fetchOutputsCatalog, type OutputsCatalogRow } from "./api/client";
import { toast } from "@/components/Toaster";
import { formatTimestamp as fmtTime } from "@/lib/formatters";
import "../admin.css";

export function OutputsCatalogPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<OutputsCatalogRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [jobTypeFilter, setJobTypeFilter] = useState("");
  const [adapterFilter, setAdapterFilter] = useState("");
  const [activeFilter, setActiveFilter] = useState("");

  useEffect(() => {
    let alive = true;
    fetchOutputsCatalog()
      .then((r) => { if (alive) setRows(r); })
      .catch(() => toast.error("Failed to load outputs catalog"))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const jobTypes = useMemo(
    () => Array.from(new Set(rows.map((r) => r.jobTypeName))).sort(),
    [rows],
  );
  const adapters = useMemo(
    () => Array.from(new Set(rows.map((r) => r.adapterType))).sort(),
    [rows],
  );

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (jobTypeFilter && r.jobTypeName !== jobTypeFilter) return false;
      if (adapterFilter && r.adapterType !== adapterFilter) return false;
      if (activeFilter === "active" && !r.isActive) return false;
      if (activeFilter === "inactive" && r.isActive) return false;
      if (q && !`${r.name} ${r.target} ${r.jobTypeName} ${r.adapterType}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [rows, search, jobTypeFilter, adapterFilter, activeFilter]);

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h1 className="admin-title">Outputs Catalog</h1>
      </div>

      {/* Filter bar — uses export-filter-bar (established in ExportHistoryPage) + rb-* tokens from admin.css */}
      <div className="export-filter-bar">
        <input
          className="rb-input"
          placeholder="Search name, target, job type…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <label>
          Job type
          <select
            className="rb-select"
            value={jobTypeFilter}
            onChange={(e) => setJobTypeFilter(e.target.value)}
          >
            <option value="">All</option>
            {jobTypes.map((j) => (
              <option key={j} value={j}>{j}</option>
            ))}
          </select>
        </label>
        <label>
          Adapter
          <select
            className="rb-select"
            value={adapterFilter}
            onChange={(e) => setAdapterFilter(e.target.value)}
          >
            <option value="">All</option>
            {adapters.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
        </label>
        <label>
          Status
          <select
            className="rb-select"
            value={activeFilter}
            onChange={(e) => setActiveFilter(e.target.value)}
          >
            <option value="">All</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </label>
      </div>

      {loading ? (
        <div className="admin-loading"><div className="loading-spinner" /></div>
      ) : rows.length === 0 ? (
        <div className="rl-empty">No export destinations configured.</div>
      ) : visible.length === 0 ? (
        <div className="rl-empty">No results match your filters.</div>
      ) : (
        <table className="roster-table">
          <thead>
            <tr>
              <th>Job Type</th>
              <th>Destination</th>
              <th>Adapter</th>
              <th>Target</th>
              <th>Format</th>
              <th>Primary</th>
              <th>Active</th>
              <th>Last Success</th>
              <th>Last Error</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((r) => {
              const dest = `/admin/forms/${r.jobTypeId}?tab=export-mapping`;
              return (
              <tr
                key={r.destinationId}
                className="roster-row"
                onClick={() => navigate(dest)}
                role="button"
                tabIndex={0}
                aria-label={`Configure ${r.name} export (${r.jobTypeName})`}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    navigate(dest);
                  }
                }}
              >
                <td>{r.jobTypeName}</td>
                <td>{r.name}</td>
                <td>{r.adapterType}</td>
                <td>{r.target || "—"}</td>
                <td>{r.formatName ?? "—"}</td>
                <td>{r.isPrimary ? "✓" : ""}</td>
                <td>{r.isActive ? "✓" : "—"}</td>
                <td>{fmtTime(r.lastSuccessAt)}</td>
                <td>
                  {r.lastError
                    ? `${fmtTime(r.lastError.at)}: ${r.lastError.message}`
                    : "—"}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
